import { useEffect, useState } from "react";
import { KeyRound, Trash2, Users, X, ArrowUp, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";

export default function PupilsAdmin() {
  const [pupils, setPupils] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [subGroups, setSubGroups] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ year_group: "", sub_group: "", parent_email: "", new_pin: "" });
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [clearOnAdvance, setClearOnAdvance] = useState(false);

  const load = () => api.get("/school/pupils").then((r) => setPupils(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    api.get("/school/profile").then((r) => {
      setYearGroups(r.data.year_groups);
      setSubGroups(r.data.sub_groups || {});
    }).catch(() => {});
  }, []);

  const advance = async () => {
    try {
      const { data } = await api.post("/school/advance-year", { clear_timetables: clearOnAdvance });
      toast.success(`Advanced ${data.advanced} pupil(s) up a year${data.top_year_unchanged ? ` — ${data.top_year_unchanged} in the final year unchanged` : ""}`);
      setAdvanceOpen(false);
      setClearOnAdvance(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({ year_group: p.year_group, sub_group: p.sub_group || "", parent_email: p.parent_email || "", new_pin: "" });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/school/pupils/${editing.id}`, {
        year_group: editForm.year_group,
        sub_group: editForm.sub_group,
        parent_email: editForm.parent_email,
        new_pin: editForm.new_pin || undefined,
      });
      toast.success(`${editing.name} updated${editForm.new_pin ? " (PIN reset)" : ""}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}? Their timetable and notes will be deleted.`)) return;
    try {
      await api.delete(`/school/pupils/${p.id}`);
      toast.success("Pupil removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyPwa = async (p) => {
    try {
      let token = p.share_enabled && p.share_token ? p.share_token : null;
      if (!token) {
        const { data } = await api.post(`/school/pupils/${p.id}/share`);
        token = data.share_token;
      }
      const ok = await copyToClipboard(`${window.location.origin}/t/${token}`);
      ok ? toast.success(`PWA link copied for ${p.name}`) : toast.error("Couldn't copy — try again");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="pupils-admin-page">
      <span className="nb-label">Pupils</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Pupil accounts</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Pupils self-register with your join code. Reset PINs, change year groups or remove accounts here.</p>

      <div className="mt-6 flex justify-end">
        <button onClick={() => setAdvanceOpen(true)} data-testid="advance-year-btn" className="nb-btn bg-[#D8B4FE] px-4 py-2 text-sm">
          <ArrowUp className="w-4 h-4" /> Advance school year…
        </button>
      </div>

      <div className="mt-4 nb-card overflow-hidden">
        <table className="w-full border-collapse" data-testid="pupils-table">
          <thead>
            <tr className="bg-zinc-900 text-white text-left">
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Name</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Year group</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Sub-group</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Parent email</th>
              <th className="p-3 text-xs font-bold tracking-wider uppercase">Joined</th>
              <th className="p-3 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {pupils.map((p) => (
              <tr key={p.id} className="border-t-2 border-zinc-900" data-testid={`pupil-row-${p.id}`}>
                <td className="p-3 font-bold">{p.name}</td>
                <td className="p-3">
                  <span className="inline-block px-2.5 py-1 rounded-lg border-2 border-zinc-900 bg-[#A7E4D3] text-xs font-bold">{p.year_group}</span>
                </td>
                <td className="p-3 text-sm font-bold">{p.sub_group || "—"}</td>
                <td className="p-3 text-sm text-zinc-500">{p.parent_email || "—"}</td>
                <td className="p-3 text-sm text-zinc-500">{new Date(p.created_at).toLocaleDateString("en-GB")}</td>
                <td className="p-3">
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => copyPwa(p)} data-testid={`pupil-pwa-${p.id}`} title="Copy PWA timetable link (for phones)"
                      className="p-2 rounded-lg border-2 border-zinc-900 bg-[#7DD3FC] hover:brightness-105 transition-colors duration-150">
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEdit(p)} data-testid={`pupil-edit-${p.id}`} title="Edit / reset PIN"
                      className="p-2 rounded-lg border-2 border-zinc-900 bg-[#FDE68A] hover:brightness-105 transition-colors duration-150">
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`pupil-delete-${p.id}`} title="Remove pupil"
                      className="p-2 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                      <Trash2 className="w-4 h-4 text-red-700" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {pupils.length === 0 && (
              <tr><td colSpan={5} className="p-10 text-center border-t-2 border-zinc-900" data-testid="pupils-empty">
                <Users className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500 text-sm">No pupils yet. Share your join code from the dashboard.</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {advanceOpen && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="advance-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-xl flex items-center gap-2"><ArrowUp className="w-5 h-5" /> Advance school year</h2>
              <button onClick={() => setAdvanceOpen(false)} data-testid="advance-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Every pupil moves up one year group (e.g. 1st Year → 2nd Year). Pupils in the final year stay put — remove their accounts manually when they leave. Sub-groups are cleared so pupils pick fresh ones.
            </p>
            <label className="flex items-start gap-2 mt-4 font-bold text-sm cursor-pointer">
              <input type="checkbox" data-testid="advance-clear-checkbox" checked={clearOnAdvance} onChange={(e) => setClearOnAdvance(e.target.checked)} className="w-4 h-4 mt-0.5 accent-zinc-900" />
              Also clear every pupil's timetable and notes (recommended for a new school year)
            </label>
            <button onClick={advance} data-testid="advance-confirm-btn" className="nb-btn w-full bg-[#D8B4FE] py-2.5 mt-5">
              <ArrowUp className="w-4 h-4" /> Advance everyone now
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="pupil-edit-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-xl">Edit {editing.name}</h2>
              <button onClick={() => setEditing(null)} data-testid="pupil-edit-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="nb-label" htmlFor="edit-year">Year group</label>
                <select id="edit-year" className="nb-input mt-2" data-testid="pupil-edit-year-group"
                  value={editForm.year_group} onChange={(e) => setEditForm({ ...editForm, year_group: e.target.value, sub_group: "" })}>
                  {yearGroups.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {(subGroups[editForm.year_group] || []).length > 0 && (
                <div>
                  <label className="nb-label" htmlFor="edit-subgroup">Sub-group</label>
                  <select id="edit-subgroup" className="nb-input mt-2" data-testid="pupil-edit-sub-group"
                    value={editForm.sub_group} onChange={(e) => setEditForm({ ...editForm, sub_group: e.target.value })}>
                    <option value="">None</option>
                    {subGroups[editForm.year_group].map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="nb-label" htmlFor="edit-email">Parent email</label>
                <input id="edit-email" type="email" className="nb-input mt-2" data-testid="pupil-edit-parent-email"
                  value={editForm.parent_email} onChange={(e) => setEditForm({ ...editForm, parent_email: e.target.value })} />
              </div>
              <div>
                <label className="nb-label" htmlFor="edit-pin">Reset PIN (leave blank to keep)</label>
                <input id="edit-pin" className="nb-input mt-2" data-testid="pupil-edit-new-pin" placeholder="New 4–8 digit PIN"
                  value={editForm.new_pin} onChange={(e) => setEditForm({ ...editForm, new_pin: e.target.value })} minLength={4} maxLength={8} />
              </div>
              <button type="submit" data-testid="pupil-edit-save" className="nb-btn w-full bg-[#1E3A8A] text-white py-2.5">Save changes</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
